import { supabase } from '../supabaseClient';

const BRANDING_BUCKET = 'club-branding';

function safeFileName(fileName) {
  return String(fileName || 'image')
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, '-')
    .replace(/-+/g, '-');
}

function createEmptyBrandingRecord() {
  return {
    id: null,
    clubName: '',
    logoUrl: '',
    heroImages: ['', '', '', '', ''],
  };
}

export async function loadClubBrandingRecord(
  organisationId
) {
  const safeOrganisationId = String(
    organisationId || ''
  ).trim();

  if (!safeOrganisationId) {
    return createEmptyBrandingRecord();
  }

  const { data, error } = await supabase
    .from('club_branding')
    .select(
      'id, organisation_id, club_name, logo_url, hero_image_1, hero_image_2, hero_image_3, hero_image_4, hero_image_5, updated_at'
    )
    .eq('organisation_id', safeOrganisationId)
    .maybeSingle();

  if (error || !data) {
    return createEmptyBrandingRecord();
  }

  return {
    id: data.id || null,
    clubName: data.club_name || '',
    logoUrl: data.logo_url || '',
    heroImages: [
      data.hero_image_1 || '',
      data.hero_image_2 || '',
      data.hero_image_3 || '',
      data.hero_image_4 || '',
      data.hero_image_5 || '',
    ],
  };
}

async function uploadBrandingFile(
  file,
  organisationId,
  folder
) {
  if (!file) return '';

  const filePath = `${organisationId}/${folder}/${Date.now()}-${safeFileName(file.name)}`;

  const { error } = await supabase.storage
    .from(BRANDING_BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  const { data } = supabase.storage
    .from(BRANDING_BUCKET)
    .getPublicUrl(filePath);

  return data?.publicUrl || '';
}

export async function saveClubBranding({
  organisationId,
  clubName,
  logoFile,
  heroFiles = [],
  existingLogoUrl = '',
}) {
  const safeOrganisationId = String(
    organisationId || ''
  ).trim();

  if (!safeOrganisationId) {
    throw new Error(
      'Could not identify the organisation for branding.'
    );
  }

  const current =
    await loadClubBrandingRecord(
      safeOrganisationId
    );

  const logoUrl = logoFile
    ? await uploadBrandingFile(
        logoFile,
        safeOrganisationId,
        'logos'
      )
    : existingLogoUrl || current.logoUrl || '';

  const heroImages = [...current.heroImages];

  while (heroImages.length < 5) {
    heroImages.push('');
  }

  for (let index = 0; index < 5; index += 1) {
    const file = heroFiles[index];

    if (file) {
      heroImages[index] =
        await uploadBrandingFile(
          file,
          safeOrganisationId,
          'hero-images'
        );
    }
  }

  const payload = {
    organisation_id:
      safeOrganisationId,
    club_name: clubName || current.clubName || '',
    logo_url: logoUrl,
    hero_image_1: heroImages[0] || null,
    hero_image_2: heroImages[1] || null,
    hero_image_3: heroImages[2] || null,
    hero_image_4: heroImages[3] || null,
    hero_image_5: heroImages[4] || null,
    updated_at: new Date().toISOString(),
  };

  if (current.id) {
    const { error } = await supabase
      .from('club_branding')
      .update(payload)
      .eq('id', current.id)
      .eq(
        'organisation_id',
        safeOrganisationId
      );

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('club_branding')
      .insert([payload]);

    if (error) throw error;
  }

  return {
    clubName: payload.club_name,
    logoUrl,
    heroImages,
  };
}